import React, { useState } from "react";
import styled from "styled-components";

const Page = styled.div`
  --bg: #07090e;
  --panel: #0d111a;
  --surface: #131824;
  --text: #f3f4f8;
  --dim: #9aa0b2;
  --faint: #636878;
  --accent: #0ea5e9;
  --danger: #f43f5e;
  --border: rgba(255, 255, 255, 0.08);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.68;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
  }
`;

const Wrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 44px 22px 90px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 24px;
  margin-bottom: 30px;
  border-bottom: 1px solid var(--border);
`;

const Badge = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: linear-gradient(145deg, #0284c7, #38bdf8);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-size: 26px;
  box-shadow: 0 8px 24px rgba(14, 165, 233, 0.35);
`;

const Brand = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;

  span {
    color: var(--accent);
  }
`;

const Title = styled.h1`
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin: 8px 0 6px;
  color: #ffffff;
`;

const Section = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 36px 0 12px;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--accent);
    border-radius: 2px;
    flex: none;
  }
`;

const Card = styled.div`
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 24px;
  margin: 18px 0 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-size: 15px;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-size: 15px;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const Button = styled.button`
  background: linear-gradient(145deg, #0284c7, #38bdf8);
  color: #ffffff;
  border: none;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  align-self: flex-start;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SuccessMsg = styled.div`
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.35);
  border-radius: 12px;
  padding: 16px;
  color: #a7f3d0;
  font-size: 15px;
  margin-top: 14px;
`;

const QaptureDelete = () => {
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitted(true);
  };

  return (
    <Page>
      <Wrap>
        <Header>
          <Badge>📡</Badge>
          <div>
            <Brand>
              ScanFi / <span>Qapture</span>
            </Brand>
            <span style={{ fontSize: "13px", color: "var(--faint)" }}>
              Data &amp; Privacy Management
            </span>
          </div>
        </Header>

        <Title>Data Deletion &amp; Management</Title>
        <p style={{ color: "var(--dim)", fontSize: "16px", marginBottom: "24px" }}>
          ScanFi / Qapture is an <strong>offline-first, 100% on-device</strong> utility application.
          We do not maintain remote user accounts or store your Wi-Fi credentials or scan records on cloud servers.
        </p>

        <Section>How to Delete Your Data Locally</Section>
        <Card>
          <p style={{ margin: "0 0 12px", color: "var(--text)", fontWeight: "600" }}>
            You have complete sovereignty over your data and can permanently delete it at any time:
          </p>
          <ol style={{ paddingLeft: "20px", color: "var(--dim)", fontSize: "15px", lineHeight: "1.7" }}>
            <li style={{ marginBottom: "8px" }}>
              <strong>Clear Stored Vault &amp; Networks:</strong> Open the app, go to <strong>Settings</strong> &rarr;{" "}
              <strong>Vault &amp; Backup</strong> &rarr; tap{" "}
              <strong style={{ color: "var(--danger)" }}>Clear All Stored Data</strong>. This instantly wipes all saved Wi-Fi networks,
              scanned barcode history, and tracked devices.
            </li>
            <li style={{ marginBottom: "8px" }}>
              <strong>Delete Individual Codes:</strong> Navigate to the <strong>Vault</strong> tab, tap any network or scanned item, and choose{" "}
              <strong>Delete</strong>.
            </li>
            <li>
              <strong>Wipe Entire App Storage:</strong> Uninstall the application or navigate to your device&apos;s system settings:{" "}
              <strong>Settings &rarr; Apps &rarr; Qapture / ScanFi &rarr; Storage &rarr; Clear Storage</strong>.
            </li>
          </ol>
        </Card>

        <Section>Submit a Formal Request</Section>
        <Card>
          <p style={{ margin: "0 0 14px", color: "var(--dim)", fontSize: "15px" }}>
            If you have questions regarding your data or need formal verification, please submit your inquiry below or contact us directly at{" "}
            <a href="mailto:hello@nimions.com" style={{ color: "var(--accent)" }}>
              hello@nimions.com
            </a>
            .
          </p>

          {submitted ? (
            <SuccessMsg>
              ✓ Thank you. Your inquiry has been received. Because ScanFi / Qapture does not collect or store personal user accounts
              on cloud databases, no remote account exists to delete. All local data is controlled directly on your device.
            </SuccessMsg>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Input
                type="email"
                placeholder="Your email address"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Textarea
                placeholder="Optional inquiry or details"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <Button type="submit">Submit Request</Button>
            </Form>
          )}
        </Card>
      </Wrap>
    </Page>
  );
};

export default QaptureDelete;
